// Default TrueNewel 90 Flat-Pack assembly booklet document.
// Data-driven: the document is one continuous, ordered list of blocks.
// A "pagebreak" block is a marker in that list — everything between two
// markers (or before the first / after the last) becomes one printed page.
// Manual markers are authored directly; additional markers get inserted
// automatically at render time wherever a page would otherwise overflow.

let c = 0;
const uid = (p = "b") => `${p}${(c++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const makeId = uid;

export const FONT_OPTIONS = {
  heading: [
    "Barlow Condensed", "Rajdhani", "Bricolage Grotesque", "Poppins", "Montserrat",
    "Oswald", "Space Grotesk", "Sora", "Archivo Black", "Playfair Display", "Bebas Neue", "Anton",
  ],
  body: [
    "Roboto", "Roboto Condensed", "Roboto Slab", "Roboto Serif", "Roboto Flex",
    "Hanken Grotesk", "Inter", "Work Sans", "Source Sans 3", "Open Sans", "Lato", "Nunito Sans",
  ],
  mono: ["Space Mono", "JetBrains Mono", "IBM Plex Mono", "Roboto Mono", "Source Code Pro"],
};

export const DEFAULT_FONTS = { heading: "Rajdhani", body: "Roboto", mono: "Space Mono" };

// Per-deployment brand defaults. This copy of the tool is pre-configured for
// TrueNewel; a fresh generic deployment would ship with neutral placeholder
// values here instead. Everything is also editable live from the in-app
// Brand settings panel and is saved with the document (doc.brand).
export const DEFAULT_BRAND = {
  name: "TrueNewel",
  accent: "#e08a1e",
  footerText: "TrueNewel · Assembly Guide",
  supportEmail: "support@truenewel.com",
  legalLine: "TrueNewel is a trading name of Syxth Limited · truenewel.com",
};

function pageBreak(tag = "") {
  return { id: uid("pb"), type: "pagebreak", tag };
}

/* Flatten the old { pages: [{ tag, blocks }] } shape into the new flat
   { blocks: [...] } shape (a leading pagebreak per former page), and fill
   in sensible defaults for anything an older saved document is missing. */
export function migrateDoc(d) {
  if (!d) return defaultDoc();
  if (Array.isArray(d.blocks)) {
    return { version: 4, logo: d.logo ?? null, fonts: { ...DEFAULT_FONTS, ...(d.fonts || {}) }, brand: { ...DEFAULT_BRAND, ...(d.brand || {}) }, blocks: d.blocks };
  }
  if (Array.isArray(d.pages)) {
    const blocks = [];
    d.pages.forEach((p) => {
      blocks.push(pageBreak(p.tag || ""));
      (p.blocks || []).forEach((b) => blocks.push(b));
    });
    return { version: 4, logo: d.logo ?? null, fonts: { ...DEFAULT_FONTS, ...(d.fonts || {}) }, brand: { ...DEFAULT_BRAND, ...(d.brand || {}) }, blocks };
  }
  return defaultDoc();
}

/* Group a flat block list (with pagebreak markers already interleaved,
   manual and/or computed) into page objects — same shape the page
   renderer has always expected: { id, tag, blocks }. */
export function groupIntoPages(blocks) {
  const pages = [];
  let current = null;
  for (const b of blocks) {
    if (b.type === "pagebreak") {
      current = { id: b.id, tag: b.tag || "", blocks: [] };
      pages.push(current);
    } else {
      if (!current) { current = { id: uid("p"), tag: "", blocks: [] }; pages.push(current); }
      current.blocks.push(b);
    }
  }
  if (!pages.length) pages.push({ id: uid("p"), tag: "", blocks: [] });
  return pages;
}

export const BLOCK_LABELS = {
  hero: "Cover header",
  section: "Section heading",
  paragraph: "Paragraph",
  step: "Numbered step",
  image: "Image slot",
  imagerow: "Image row",
  callout: "Callout",
  kitlist: "Kit list",
  checklist: "Checklist",
  features: "Feature list",
  timebar: "Time bar",
  support: "Support card",
  qrcode: "QR code",
};

// Shared defaults applied to every image slot so resize/zoom controls
// always have a sensible starting point.
export const IMAGE_DEFAULTS = { scale: 100, zoom: 100, panX: 0, panY: 0 };

function makeKitItem(qty = "1×", label = "Item") {
  return { id: uid("i"), qty, label, image: null };
}

export function makeBlock(type, brand = DEFAULT_BRAND) {
  switch (type) {
    case "hero":
      return { id: uid(), type, model: "MODEL · REV. A", title: "Assembly\nGuide", subtitle: `Everything you need to build your ${brand.name} product.` };
    case "section":
      return { id: uid(), type, eyebrow: "Section", title: "New section title", stageNum: "" };
    case "paragraph":
      return { id: uid(), type, variant: "body", text: "New paragraph text." };
    case "step":
      return { id: uid(), type, num: "1", title: "Step title", text: "Describe this step.", image: { enabled: true, side: "right", aspect: "4:3", code: "0.0", label: "Diagram", src: null, ...IMAGE_DEFAULTS }, callout: { enabled: false, ctype: "tip", label: "Tip:", text: "Helpful note." } };
    case "image":
      return { id: uid(), type, size: "normal", code: "FIG", label: "Diagram", caption: "Caption", src: null, ...IMAGE_DEFAULTS };
    case "imagerow":
      return { id: uid(), type, title: "Image row", cols: 3, aspect: "4:3", slots: [
        { id: uid("s"), code: "A", label: "Slot", caption: "Caption", src: null, ...IMAGE_DEFAULTS },
        { id: uid("s"), code: "B", label: "Slot", caption: "Caption", src: null, ...IMAGE_DEFAULTS },
        { id: uid("s"), code: "C", label: "Slot", caption: "Caption", src: null, ...IMAGE_DEFAULTS },
      ] };
    case "callout":
      return { id: uid(), type, ctype: "tip", label: "Tip:", text: "Helpful note." };
    case "kitlist":
      return { id: uid(), type, title: "In this kit", items: [makeKitItem()] };
    case "checklist":
      return { id: uid(), type, variant: "tools", title: "Checklist", items: [{ id: uid("i"), label: "Item" }] };
    case "features":
      return { id: uid(), type, numbered: false, items: [{ id: uid("i"), title: "Feature", text: "Description.", bulleted: false, bullets: null, image: null }] };
    case "timebar":
      return { id: uid(), type, bold: "About 20 minutes", sub: "Plus setting time." };
    case "support":
      return { id: uid(), type, line: "Questions during your build?", email: brand.supportEmail, legal: brand.legalLine };
    case "qrcode":
      return { id: uid(), type, numbered: false, num: "1", title: "Scan for a video guide", text: "Point your camera at the code for a short walkthrough.", url: "https://example.com/support", size: 28 };
    default:
      return { id: uid(), type: "paragraph", variant: "body", text: "" };
  }
}

// Minimal, brand-driven generic starter — deliberately not tied to any one
// product. Load a real project's saved manual from the Manuals library (or
// Import a JSON template) rather than editing this starter for content.
export function defaultDoc() {
  const brand = DEFAULT_BRAND;
  const pages = [
      { id: uid("p"), tag: "", showFooter: true, blocks: [
        { id: uid(), type: "hero", model: "MODEL · REV. A", title: "Assembly\nGuide", subtitle: `Everything you need to build your ${brand.name} product.` },
        { id: uid(), type: "image", size: "normal", maxH: 72, code: "FIG.00", label: "Hero image", caption: "The finished product", src: null, ...IMAGE_DEFAULTS },
        { id: uid(), type: "kitlist", title: "In this kit", items: [makeKitItem("1×", "Item 1"), makeKitItem("1×", "Item 2")] },
      ] },
      { id: uid("p"), tag: "Before you begin", showFooter: true, blocks: [
        { id: uid(), type: "section", eyebrow: "Get set up", title: "What you need & what's in the box", stageNum: "" },
        { id: uid(), type: "checklist", variant: "tools", title: "Tools you'll need", items: [{ id: uid("i"), label: "Item" }] },
        { id: uid(), type: "timebar", bold: "About 20 minutes to assemble", sub: "" },
      ] },
      { id: uid("p"), tag: "Stage 1", showFooter: true, blocks: [
        { id: uid(), type: "section", eyebrow: "Stage 1", title: "Section title", stageNum: "1" },
        { id: uid(), type: "step", num: "1", title: "Step title", text: "Describe this step.", image: { enabled: true, side: "right", code: "1.1", label: "Diagram", src: null, ...IMAGE_DEFAULTS }, callout: { enabled: false, ctype: "tip", label: "Tip:", text: "" } },
      ] },
      { id: uid("p"), tag: "Finishing up", showFooter: true, blocks: [
        { id: uid(), type: "section", eyebrow: "You're ready", title: "You're all set", stageNum: "" },
        { id: uid(), type: "support", line: "Questions during your build?", email: brand.supportEmail, legal: brand.legalLine },
      ] },
  ];

  const blocks = [];
  pages.forEach((p) => { blocks.push(pageBreak(p.tag || "")); p.blocks.forEach((b) => blocks.push(b)); });
  return { version: 4, logo: null, fonts: DEFAULT_FONTS, brand: { ...brand }, blocks };
}
